interface QuestLabelParams {
  name: string;
  label?: string | null;
}

interface BuildQuestLabelParams extends QuestLabelParams {
  isCustomizeButtonText?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object');
}

function hasUsableQuestId(value: unknown): value is { id: number | string } {
  if (!isRecord(value)) return false;

  const { id } = value;

  if (typeof id === 'number') return Number.isFinite(id);

  return typeof id === 'string' && id.trim().length > 0;
}

function findQuestResponseCandidate(payload: unknown): unknown {
  if (hasUsableQuestId(payload)) {
    return payload;
  }

  if (!isRecord(payload)) {
    return undefined;
  }

  if (hasUsableQuestId(payload.quest)) {
    return payload.quest;
  }

  if ('data' in payload) {
    return findQuestResponseCandidate(payload.data);
  }

  return undefined;
}

export function getQuestButtonLabel({ name, label }: QuestLabelParams) {
  const trimmedLabel = typeof label === 'string' ? label.trim() : '';

  return trimmedLabel || name;
}

export function isCustomQuestButtonLabel(quest: QuestLabelParams) {
  const questLabel = getQuestButtonLabel(quest);

  return questLabel !== quest.name;
}

export function buildQuestLabel({ name, label, isCustomizeButtonText }: BuildQuestLabelParams) {
  if (!isCustomizeButtonText) {
    return name;
  }

  return getQuestButtonLabel({ name, label });
}

export function normalizeQuestLabel<T extends QuestLabelParams>(
  quest: T
): Omit<T, 'label'> & { label: string } {
  return {
    ...quest,
    label: getQuestButtonLabel(quest)
  };
}

export function extractQuestResponseData<T extends { id: number | string } = Quest>(
  payload: unknown
): T | undefined {
  const candidate = findQuestResponseCandidate(payload);

  if (!hasUsableQuestId(candidate)) {
    return undefined;
  }

  if (isRecord(candidate)) {
    const candidateRecord = candidate as Record<string, unknown>;

    if (typeof candidateRecord.name === 'string') {
      return normalizeQuestLabel(
        candidateRecord as unknown as T & QuestLabelParams
      ) as unknown as T;
    }
  }

  return candidate as T;
}
